import { useEffect, useState } from "react";
import { useRequest } from "../hooks/useRequest";
import {
  getUsageStats,
  exportUsageCsv,
  UsageQuery,
  UsageStat,
} from "../api/usage";
import { formatCents, formatNumber } from "../utils/format";
import { downloadBlob } from "../utils/download";
import "./UsageStats.scss";

/** Endpoint 下拉选项（value 为 api_code，"" 表示全部） */
const ENDPOINT_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "All Endpoints" },
  { value: "product.detail", label: "product.detail" },
  { value: "product.discover", label: "product.discover" },
  { value: "checkout.order", label: "checkout.order" },
];

/** yyyy-mm-dd → ISO instant（含边界时刻） */
function dateToInstant(date: string, endOfDay = false): string | undefined {
  if (!date) return undefined;
  const d = new Date(`${date}T${endOfDay ? "23:59:59" : "00:00:00"}`);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

/** 成功率 → 颜色类名：100%=绿，60%及以上=黄，60%以下=红 */
function rateClass(rate: number): string {
  if (rate >= 100) return "cs-usage__rate--good";
  if (rate >= 60) return "cs-usage__rate--warn";
  return "cs-usage__rate--bad";
}

const UsageStats = () => {
  useEffect(() => {
    document.title = "Usage Stats | CloudStore API";
  }, []);

  // 已提交（生效中）的查询条件；默认空，由后端取近 7 天
  const [query, setQuery] = useState<UsageQuery>({});

  // 表单输入
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [endpoint, setEndpoint] = useState("");

  const [exporting, setExporting] = useState(false);
  const [exportErr, setExportErr] = useState<string | null>(null);

  const { data, loading, error } = useRequest(
    () => getUsageStats(query),
    [query]
  );

  const rows = data ?? [];

  const handleSearch = () => {
    setQuery({
      from: dateToInstant(dateFrom),
      to: dateToInstant(dateTo, true),
      endpoint: endpoint || undefined,
    });
  };

  const handleReset = () => {
    setDateFrom("");
    setDateTo("");
    setEndpoint("");
    setQuery({});
  };

  const handleExport = async () => {
    setExportErr(null);
    setExporting(true);
    try {
      const blob = await exportUsageCsv({
        from: dateToInstant(dateFrom),
        to: dateToInstant(dateTo, true),
      });
      downloadBlob(blob, "usage-export.csv");
    } catch (e) {
      setExportErr(e instanceof Error ? e.message : "Export failed.");
    } finally {
      setExporting(false);
    }
  };

  const rate = (r: UsageStat): number =>
    r.calls > 0 ? (r.success_count / r.calls) * 100 : 0;

  return (
    <div className="cs-usage">
      <h1 className="cs-usage__title">Usage Stats</h1>

      {/* 筛选栏 */}
      <div className="cs-usage__filters">
        <div className="cs-usage__field">
          <span className="cs-usage__field-label">Date From</span>
          <input
            type="date"
            className="cs-usage__date"
            value={dateFrom}
            max={dateTo || undefined}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </div>
        <div className="cs-usage__field">
          <span className="cs-usage__field-label">Date To</span>
          <input
            type="date"
            className="cs-usage__date"
            value={dateTo}
            min={dateFrom || undefined}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>
        <div className="cs-usage__field">
          <span className="cs-usage__field-label">Endpoint</span>
          <select
            className="cs-usage__select"
            value={endpoint}
            onChange={(e) => setEndpoint(e.target.value)}
          >
            {ENDPOINT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <button
          type="button"
          className="cs-usage__btn cs-usage__btn--primary"
          onClick={handleSearch}
        >
          Search
        </button>
        <button
          type="button"
          className="cs-usage__btn cs-usage__btn--ghost"
          onClick={handleReset}
        >
          Reset
        </button>
        <button
          type="button"
          className="cs-usage__btn cs-usage__btn--primary"
          onClick={handleExport}
          disabled={exporting}
        >
          {exporting ? "Exporting…" : "Export CSV"}
        </button>
      </div>

      {exportErr && <div className="cs-usage__error">{exportErr}</div>}

      {/* 统计表 */}
      <div className="cs-usage__table-wrap">
        <table className="cs-usage__table">
          <thead>
            <tr>
              <th className="cs-usage__th">Date</th>
              <th className="cs-usage__th">Endpoint</th>
              <th className="cs-usage__th">Calls</th>
              <th className="cs-usage__th">Cost</th>
              <th className="cs-usage__th">Success</th>
              <th className="cs-usage__th">Failed</th>
              <th className="cs-usage__th">Rate</th>
              <th className="cs-usage__th">Latency</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td className="cs-usage__state" colSpan={8}>
                  Loading…
                </td>
              </tr>
            )}
            {!loading && error && (
              <tr>
                <td className="cs-usage__state cs-usage__state--error" colSpan={8}>
                  {error}
                </td>
              </tr>
            )}
            {!loading && !error && rows.length === 0 && (
              <tr>
                <td className="cs-usage__state" colSpan={8}>
                  No usage data for this period.
                </td>
              </tr>
            )}
            {!loading &&
              !error &&
              rows.map((r, i) => {
                const rt = rate(r);
                return (
                  <tr key={`${r.date}-${r.api_code}-${i}`}>
                    <td className="cs-usage__td">{r.date}</td>
                    <td className="cs-usage__td cs-usage__endpoint">
                      {r.api_code}
                    </td>
                    <td className="cs-usage__td">{formatNumber(r.calls)}</td>
                    <td className="cs-usage__td">{formatCents(r.cost_cents)}</td>
                    <td className="cs-usage__td">
                      {formatNumber(r.success_count)}
                    </td>
                    <td className="cs-usage__td">
                      {formatNumber(r.failed_count)}
                    </td>
                    <td className="cs-usage__td">
                      <span
                        className={`cs-usage__rate ${rateClass(rt)}`}
                      >
                        {rt.toFixed(1)}%
                      </span>
                    </td>
                    <td className="cs-usage__td">{r.avg_latency_ms}ms</td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UsageStats;
